import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const chapters = [
  { number: 1, title: 'Money Traps, Overspending, and Lifestyle Creep', description: 'Most overspending isn\'t random. It\'s driven by stress, comparison, convenience, and small habits that quietly stack up. Spot the patterns so you can start plugging the leaks.' },
  { number: 2, title: 'Budgeting That Fits Real Life', description: 'A budget isn\'t a cage. It\'s a map that keeps you from getting lost. Build one that flexes around the life you actually live, not an idealized version of yourself.' },
  { number: 3, title: 'The Five Cs of Credit', description: 'Your credit score isn\'t who you are. It\'s a story told about you, in a language nobody teaches you to read. Learn what lenders and landlords are actually reading, and how to write a better one.' },
  { number: 4, title: 'APR, Interest, and Why Debt Feels So Hard to Escape', description: 'What APR actually means, how compounding works against you, and why minimum payments barely move the balance. Explained calmly, the way someone should have explained it the first time.' },
  { number: 5, title: 'Saving, Compound Growth, and Why "Future You" Still Matters', description: 'Saving isn\'t just about the number in your bank account. It\'s about giving yourself breathing room. Start in a way that actually fits your life, even when margins are thin.' },
  { number: 6, title: 'Debt Payoff: Turning Stress into a Plan You Can Actually Use', description: 'You can\'t think your way out of debt. You have to plan your way out. Move from minimum-payment anxiety to a workable plan so the balance stops following you around in the back of your mind.' },
];

export function TableOfContents() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-20 sm:py-28 bg-bg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-text">
            What's inside
          </h2>
          <p className="mt-4 text-text-light text-lg">
            Six chapters, each with reflection prompts and a worksheet. Read one. Pause. Make one change. Move on.
          </p>
        </motion.div>

        <div className="space-y-3">
          {chapters.map((chapter, i) => (
            <motion.div
              key={chapter.number}
              className="bg-white rounded-lg border border-sage/10 overflow-hidden"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between p-5 text-left hover:bg-sage/5 transition-colors"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
              >
                <div className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-sage/10 text-sage-dark font-serif font-bold flex items-center justify-center text-sm">
                    {chapter.number}
                  </span>
                  <span className="font-serif text-lg font-medium text-text">
                    {chapter.title}
                  </span>
                </div>
                <svg
                  aria-hidden="true"
                  className={`w-5 h-5 text-text-light flex-shrink-0 transition-transform ${openIndex === i ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p className="px-5 pb-5 pl-19 text-text-light leading-relaxed">
                      {chapter.description}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
